/**
 * Pantalla de **Activar inventario por sede** (`/inventario/activar-por-sede`).
 * Reemplaza el `PlaceholderPage` de `ROUTES.inventarioAdmin`. Equivale a
 * `admin_inventario.html` del SSR.
 *
 * Solo-admin (`RequireInventario mode="admin"`, igual que el SSR y el menú). Es
 * una vista **global**: lista TODAS las sedes activas con el estado del módulo de
 * inventario y del horario láser, y permite activarlos/desactivarlos por sede
 * (`POST /inventario/admin/toggle` y `.../laser/toggle`). El overview no usa la
 * sede activa; el toggle apunta a la sede de la fila (el hook fija el `X-Sede-Id`).
 *
 * Orquesta hooks + tablas presentacionales; sin `fetch`. Estados de carga/error
 * contemplados; el error de un toggle se muestra como aviso.
 */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";

import { inventarioErrorMessage } from "../format";
import { useAdminOverview } from "../hooks/useInventarioData";
import {
  useToggleInventario,
  useToggleLaser,
} from "../hooks/useInventarioMutations";
import type { AdminModule } from "../types";
import { AdminSedeTable } from "./AdminSedeTable";
import { RequireInventario } from "./RequireInventario";

export function InventarioAdminScreen() {
  const overviewQuery = useAdminOverview();
  const toggleInventario = useToggleInventario();
  const toggleLaser = useToggleLaser();

  /** Despacha el toggle del módulo indicado a su mutación. */
  function handleToggle(module: AdminModule, sedeId: number) {
    if (module === "inventario") {
      toggleInventario.mutate(sedeId);
    } else {
      toggleLaser.mutate(sedeId);
    }
  }

  // Id de la sede cuyo toggle está en curso (para deshabilitar solo esa fila).
  const pendingInvSede =
    toggleInventario.isPending ? toggleInventario.variables ?? null : null;
  const pendingLaserSede =
    toggleLaser.isPending ? toggleLaser.variables ?? null : null;

  const toggleError = toggleInventario.isError
    ? inventarioErrorMessage(toggleInventario.error)
    : toggleLaser.isError
      ? inventarioErrorMessage(toggleLaser.error)
      : null;

  return (
    <RequireInventario mode="admin" title="Activar inventario por sede">
      <div className="flex flex-col gap-6">
        <h1 className="text-2xl font-semibold text-slate-900">Módulos · Activación por sede</h1>

        {toggleError ? (
          <p className="text-sm text-red-600" role="alert">
            {toggleError}
          </p>
        ) : null}

        {overviewQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando sedes…
              </p>
            </CardContent>
          </Card>
        ) : overviewQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {inventarioErrorMessage(overviewQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Inventario</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-sm text-slate-500">
                  Los usuarios con <code>view_inventory</code> podrán verlo; con{" "}
                  <code>manage_inventory</code> pueden editar el stock.
                </p>
                <AdminSedeTable
                  rows={overviewQuery.data.items}
                  module="inventario"
                  label="Inventario por sede"
                  pendingSedeId={pendingInvSede}
                  onToggle={(sedeId) => handleToggle("inventario", sedeId)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Horario Láser</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-3 text-sm text-slate-500">
                  Solo se muestra el acceso en las sedes donde esté activo. Los
                  usuarios con permiso <code>laser_schedule</code> lo ven si la sede
                  lo tiene habilitado.
                </p>
                <AdminSedeTable
                  rows={overviewQuery.data.items}
                  module="laser"
                  label="Horario láser por sede"
                  pendingSedeId={pendingLaserSede}
                  onToggle={(sedeId) => handleToggle("laser", sedeId)}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </RequireInventario>
  );
}
